import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { CookieService } from '~/common/utils/cookies';
import gsap from 'gsap';
import { CUSTOM_EVENTS } from '~/common/constants/events';
// Imported from the leaf, not the `cookie` barrel: the barrel also pulls in the full
// UK and EU cookie-policy prose, and this banner renders in the root layout — so the
// barrel would drag ~3.4 KB gzipped of /cookie-only content into the initial payload.
import COOKIE_BANNER_TEXT from '~/common/constants/cookie/banner';
import { fontFamilies } from '~/theme/typography';
import { useNavigation } from '~/common/hooks/useNavigation';
import { useRegion } from '~/recoil/useRegion';

const CookiesBanner = () => {
  const { goToCookie } = useNavigation();
  const { region } = useRegion();
  const [showBanner, setShowBanner] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (CookieService.hasValidConsent()) return;

    const timer = setTimeout(() => {
      setShowBanner(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const onConsentUpdated = () => {
      if (CookieService.hasValidConsent() && showBanner) closeBanner();
    };

    window.addEventListener(
      CUSTOM_EVENTS.cookieConsentUpdated,
      onConsentUpdated,
    );
    return () =>
      window.removeEventListener(
        CUSTOM_EVENTS.cookieConsentUpdated,
        onConsentUpdated,
      );
  }, [showBanner]);

  useEffect(() => {
    if (showBanner && bannerRef.current) {
      const ctx = gsap.context(() => {
        gsap.fromTo(
          bannerRef.current,
          { y: 100, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' },
        );
      }, bannerRef);

      return () => ctx.revert();
    }
  }, [showBanner]);

  const closeBanner = () => {
    if (bannerRef.current) {
      gsap.to(bannerRef.current, {
        y: 100,
        opacity: 0,
        duration: 0.4,
        ease: 'power3.in',
        onComplete: () => setShowBanner(false),
      });
    } else setShowBanner(false);
  };

  const handleAcceptAll = () => {
    CookieService.setConsent({
      analytics: true,
      functionality: true,
      marketing: true,
      ts: new Date().toISOString(),
    });

    window.dispatchEvent(new Event(CUSTOM_EVENTS.cookieConsentUpdated));
    closeBanner();
  };

  const handleRejectAll = () => {
    CookieService.setConsent({
      analytics: false,
      functionality: false,
      marketing: false,
      ts: new Date().toISOString(),
    });

    window.dispatchEvent(new Event(CUSTOM_EVENTS.cookieConsentUpdated));
    closeBanner();
  };

  const handleManagePreferences = () => {
    window.dispatchEvent(new Event(CUSTOM_EVENTS.openCookiePreferences));
  };

  if (!showBanner) return null;

  return (
    <BannerWrapper ref={bannerRef}>
      <BannerContent>
        <BannerText>
          {COOKIE_BANNER_TEXT[region]}{' '}
          <CookiePolicyLink
            tabIndex={0}
            role="link"
            onClick={(e) => {
              e.preventDefault();
              goToCookie();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                goToCookie();
              }
            }}
          >
            LEARN MORE
          </CookiePolicyLink>
        </BannerText>
        <ButtonRow>
          <SecondaryButton onClick={handleManagePreferences}>
            Manage Preferences
          </SecondaryButton>
          <SecondaryButton onClick={handleRejectAll}>
            Reject All
          </SecondaryButton>
          <PrimaryButton onClick={handleAcceptAll}>Accept All</PrimaryButton>
        </ButtonRow>
      </BannerContent>
    </BannerWrapper>
  );
};

export default CookiesBanner;

const BannerWrapper = styled.div`
  position: fixed;
  bottom: 2.5rem;
  left: 1rem;
  right: 1rem;
  z-index: ${({ theme }) => theme.zIndex.modal};
  display: flex;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  /* Account for iPhone safe areas */
  padding-left: env(safe-area-inset-left, 0px);
  padding-right: env(safe-area-inset-right, 0px);

  @media ${({ theme }) => theme.breakpoints.md} {
    bottom: calc(1.5rem + env(safe-area-inset-bottom, 0px));
  }
`;

const BannerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 1006px;
  width: 100%;
  padding: 1.5rem;
  background-color: ${({ theme }) => theme.colors.colorMain};
  border-radius: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.white};
  pointer-events: auto;
`;

const BannerText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.CircularStd};
  font-size: 1rem;
  letter-spacing: 0.15px;
  text-transform: uppercase;
`;

const CookiePolicyLink = styled.span`
  text-decoration: underline;
  text-decoration-skip-ink: none;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const ButtonRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media ${({ theme }) => theme.breakpoints.md} {
    display: flex;
    flex-direction: column-reverse;
    gap: 16px;
  }
`;

const BaseButton = styled.button`
  height: 42px;
  width: 100%;
  padding: 0 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  font-family: ${fontFamilies.CircularStd};
  font-size: 1rem;
  line-height: 1;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
`;

const PrimaryButton = styled(BaseButton)`
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.colorMain};
  border: none;
`;

const SecondaryButton = styled(BaseButton)`
  background-color: transparent;
  color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.white};
`;
